/**
 * 登录页「作业流水线」视觉件(2026-08-27,老板「登录页太空洞」批,方案 C)。
 *
 * ★三条硬约束,改这个文件前先读:
 *
 * 1. **零新依赖**。特效全部是 CSS(漂移栅格 + transform 位移),不引 ogl / three / gsap,
 *    不进 package.json,不动 vite 分包。原因:0826a 分包改动导致过整站白屏,而 200 状态码、
 *    chunk 数、词面锚点全绿,只有真浏览器打开才看得见。登录页是全站唯一的必经页,
 *    不值得为一个背景纹理承担那个风险。
 *
 * 2. **只用皮肤 token,不写死颜色**。standard 皮肤是浅底(白面 + 蓝主色),
 *    cyber 是深底霓虹。任何 `#22DFF2` 这种字面量都会在另外两套皮肤下瞎掉。
 *    一律走 `hsl(var(--primary))` / `--chart-2` / `--foreground` / `--background`。
 *
 * 3. **动画只改 transform / opacity**(仓规)。栅格漂移是给一个超尺寸层做 translate3d,
 *    不是动 background-position;数据包是 translateX,不是动 left。
 *
 * ★为什么 keyframes 写在组件里而不是 index.css:这批的范围是「只改登录页」,
 *   往全局样式表里加东西会让别的 session 的上批锚点复核变难判。整页只渲染一次,不会重复注入。
 */

/** 栅格格距(px)。漂移距离必须**正好等于**它,否则循环会看见跳帧。 */
const CELL = 26;

/**
 * 五道工序 —— **本轮按要求写死**(2026-08-27 用户:「先写死,后面再换」)。
 *
 * ★真值源在 `@/entities/capability` 的 `CLUSTER_ORDER` / `CLUSTER_META`,文案在
 *   common.json 的 `cluster.<key>` / `cluster.<key>Sub`。这里是**副本**,存在漂移风险:
 *   有人改了 cluster.contentSub,登录页不会跟着变,而且没有任何判据会红。
 *   要接回去就是把下面这个数组换成 `CLUSTER_ORDER.map(c => ({ ... t(CLUSTER_META[c].labelKey) ... }))`,
 *   一处改动,不需要动版面。
 *
 * ★注意只列**群**不列**舱**:舱归属由后端 xb_module_catalog 决定、运营可改不发版
 *   (见 entities/capability/model/clusters.ts 的注释),写死舱名的那天就是登录页和产品对不上的那天。
 */
const PIPELINE_STATIONS = [
  { key: "content", name: "内容生产", tasks: "做图 / 做视频 / 写文案 / 拆爆款" },
  // 0926 建站舱暂停开放(catalog coming_soon)→ 登录页不再拿「建官网」招徕;换成获客舱真有的「挖商机」。
  { key: "acquisition", name: "获客推广", tasks: "查曝光 / 找客户 / 挖商机" },
  { key: "conversion", name: "成交服务", tasks: "自动答疑 / 练话术 / 接待逼单" },
  { key: "intelligence", name: "情报决策", tasks: "看经营数据 / 出体检报告 / 盯对手" },
  { key: "operations", name: "运营", tasks: "多平台统一发 / 统一看数" },
] as const;

/** 三个数据包错峰跑,颜色各自取一个 chart token(三套皮肤都有定义)。 */
const PACKETS = [
  { token: "--primary", delay: "0s" },
  { token: "--chart-2", delay: "1.8s" },
  { token: "--chart-3", delay: "3.6s" },
];

/**
 * 背景层:漂移栅格 + 暗角。
 * 绝对定位铺满父容器,`pointer-events-none` 保证不吃点击(登录表单在它上面)。
 */
export function LoginBackdrop() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      {/* 漂移栅格。层做成 200% 并向左上平移一格,循环处无缝。 */}
      <div
        className="xb-grid-drift absolute -inset-[60px]"
        style={{
          backgroundImage: `linear-gradient(hsl(var(--primary) / 0.10) 1px, transparent 1px),
                            linear-gradient(90deg, hsl(var(--primary) / 0.10) 1px, transparent 1px)`,
          backgroundSize: `${CELL}px ${CELL}px`,
        }}
      />
      {/* 暗角:把四周压下去,让中间的文字和登录卡浮出来。用 --background 保证三皮肤都对。 */}
      <div
        className="absolute inset-0"
        style={{
          background: `radial-gradient(1100px 620px at 32% 42%, transparent 6%, hsl(var(--background) / 0.86) 76%)`,
        }}
      />
    </div>
  );
}

/**
 * 底部流水线:五道工序 + 一条连接线 + 三个跑动的数据包。
 *
 * ★连接线只在 lg 以上出现 —— 窄屏下工序是多行网格,横穿一条线没有任何意义,
 *   还会从第 3 个工序的脸上划过去。
 */
export function LoginPipeline() {
  return (
    <div className="w-full">
      <ol className="grid grid-cols-2 gap-x-6 gap-y-7 text-center sm:grid-cols-3 lg:grid-cols-5">
        {PIPELINE_STATIONS.map((s) => (
          <li key={s.key}>
            <p className="text-lg font-semibold text-foreground lg:text-xl">{s.name}</p>
            <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{s.tasks}</p>
          </li>
        ))}
      </ol>

      {/* 线 + 节点 + 数据包。h-4 给节点(16px)留出行高,节点垂直居中压在线上。 */}
      <div className="relative mt-7 hidden h-4 lg:block">
        <div
          className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2"
          style={{
            background: `linear-gradient(90deg, hsl(var(--primary) / 0), hsl(var(--primary) / 0.42) 12%,
                         hsl(var(--chart-2) / 0.42) 88%, hsl(var(--chart-2) / 0))`,
          }}
        />
        {/* 节点用同一套 5 列栅格,列心自动对齐上面的工序名,不靠手算百分比。 */}
        <div className="absolute inset-0 grid grid-cols-5">
          {PIPELINE_STATIONS.map((s) => (
            <span key={s.key} className="flex items-center justify-center">
              <span
                className="h-4 w-4 rounded-full border-2 bg-background"
                style={{
                  borderColor: "hsl(var(--primary) / 0.85)",
                  boxShadow: "0 0 12px hsl(var(--primary) / 0.5)",
                }}
              />
            </span>
          ))}
        </div>
        {/* 数据包:载体层本身是满宽,translateX(100%) 正好等于走完全程;
            药丸挂在载体左沿外侧,起点在画面外。 */}
        <div className="absolute inset-0 overflow-hidden">
          {PACKETS.map((p) => (
            <div
              key={p.token}
              className="xb-packet absolute inset-y-0 left-0 w-full"
              style={{ animationDelay: p.delay }}
            >
              <span
                className="absolute top-1/2 h-3 w-11 -translate-y-1/2 rounded-full"
                style={{
                  left: `-44px`,
                  background: `linear-gradient(90deg, hsl(var(${p.token}) / 0), hsl(var(${p.token})))`,
                  boxShadow: `0 0 18px hsl(var(${p.token}) / 0.8)`,
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * 本页用到的 keyframes。
 * ★全部包在 `prefers-reduced-motion: no-preference` 里 —— 默认不动,只有用户没要求减少动效时才动。
 *   这样写比「reduce 时再关掉」安全:漏一条也只是不动,不会变成晃个不停。
 */
export function LoginFxStyles() {
  return (
    <style>{`
      @media (prefers-reduced-motion: no-preference) {
        .xb-grid-drift { animation: xb-grid-drift 5.2s linear infinite; }
        .xb-packet     { animation: xb-packet 5.4s linear infinite; }
        .xb-shine      { animation: xb-shine 5s linear infinite; }
      }
      @keyframes xb-grid-drift {
        from { transform: translate3d(0, 0, 0); }
        to   { transform: translate3d(-${CELL}px, -${CELL}px, 0); }
      }
      @keyframes xb-packet {
        0%   { transform: translateX(0);    opacity: 0; }
        6%   { opacity: 1; }
        94%  { opacity: 1; }
        100% { transform: translateX(100%); opacity: 0; }
      }
      @keyframes xb-shine {
        from { background-position: 150% 0; }
        to   { background-position: -50% 0; }
      }
    `}</style>
  );
}
